(function () {
  'use strict';

  const STORAGE_KEY = 'starknight_selected_vehicle';
  const HISTORY_STORAGE_KEY = 'starknight_vehicle_history';
  const MAX_LOCAL_RIDES = 8;
  const BUTTON_ID = 'sk-header-vehicle-trigger';
  const MODAL_ID = 'sk-vehicle-modal';

  let initialized = false;

  function textOf(element) {
    return element
      ? String(element.textContent || '').replace(/\s+/g, ' ').trim()
      : '';
  }

  function normalizeVehicleLabel(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function readLocalRides() {
    try {
      const saved = JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY) || '[]');

      if (!Array.isArray(saved)) return [];

      return saved.map(function (ride) {
        if (typeof ride === 'string') {
          return { label: ride, year: '', make: '', model: '' };
        }

        return {
          label: String(ride?.label || '').trim(),
          year: String(ride?.year || '').trim(),
          make: String(ride?.make || '').trim(),
          model: String(ride?.model || '').trim()
        };
      }).filter(function (ride) {
        return ride.label && /\b(?:19|20)\d{2}\b/.test(ride.label);
      });
    } catch (error) {
      return [];
    }
  }

  function writeLocalRides(rides) {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(rides));
    } catch (error) {
      console.warn('[SK Vehicle Search] Local vehicle history could not be saved.');
    }
  }

  function saveLocalRide(values) {
    const ride = {
      year: String(values[0] || '').trim(),
      make: String(values[1] || '').trim(),
      model: String(values[2] || '').trim()
    };

    ride.label = [ride.year, ride.make, ride.model].filter(Boolean).join(' ');

    if (!ride.year || !ride.make || !ride.model) return null;

    const key = normalizeVehicleLabel(ride.label);
    const rides = readLocalRides().filter(function (savedRide) {
      return normalizeVehicleLabel(savedRide.label) !== key;
    });

    rides.unshift(ride);
    writeLocalRides(rides.slice(0, MAX_LOCAL_RIDES));

    return ride;
  }

  function removeLocalRide(label) {
    const key = normalizeVehicleLabel(label);
    const rides = readLocalRides().filter(function (ride) {
      return normalizeVehicleLabel(ride.label) !== key;
    });

    writeLocalRides(rides);
  }

  function parseLegacyVehicle(label) {
    const match = String(label || '').trim().match(/^((?:19|20)\d{2})\s+(.+)$/);

    if (!match) return null;

    const remaining = match[2];
    const knownMakes = [
      'Arctic Cat', 'John Deere', 'Can-Am', 'Polaris', 'Honda', 'Kawasaki',
      'Yamaha', 'CFMOTO', 'Kubota', 'Massimo', 'Segway', 'Hisun', 'Kymco'
    ];
    const make = knownMakes.find(function (candidate) {
      return remaining.toLowerCase().startsWith(candidate.toLowerCase() + ' ');
    }) || remaining.split(' ')[0];
    const model = remaining.slice(make.length).trim();

    if (!model) return null;

    return { label: String(label).trim(), year: match[1], make: make, model: model };
  }

  function getThemeConfig() {
    const fallback = {
      loggedIn: null,
      loginUrl: '/account/login',
      garageUrl: '/pages/my-garage',
      shopUrl: '/collections/all'
    };
    const configElement = document.getElementById('sk-vehicle-search-config');

    if (!configElement) return fallback;

    try {
      return Object.assign(fallback, JSON.parse(configElement.textContent || '{}'));
    } catch (error) {
      console.warn('[SK Vehicle Search] Theme config could not be read.');
      return fallback;
    }
  }

  function buildLoginUrl(config) {
    const loginUrl = new URL(config.loginUrl || '/account/login', window.location.origin);
    loginUrl.searchParams.set(
      'return_url',
      window.location.pathname + window.location.search
    );
    return loginUrl.pathname + loginUrl.search;
  }

  function isCustomerLoggedIn(config) {
    const hasLogoutControl = Boolean(
      document.querySelector(
        'a[href*="/account/logout"], form[action*="/account/logout"]'
      )
    );

    /*
     * Liquid 的 customer 状态是唯一权威来源。
     * Shopify preview / analytics 元数据中可能出现 customerId，
     * 但它不一定代表店铺顾客已经登录，所以不能用于隐藏登录入口。
     */
    if (typeof config.loggedIn === 'boolean') return config.loggedIn;

    return hasLogoutControl;
  }

  function updateAccountRow(dialog) {
    const config = getThemeConfig();
    const accountLink = dialog.querySelector('[data-sk-garage-account-link]');
    const accountLabel = dialog.querySelector('[data-sk-garage-account-label]');

    if (!accountLink || !accountLabel) return;

    const loggedIn = isCustomerLoggedIn(config);

    accountLink.href = loggedIn
      ? (config.garageUrl || '/pages/my-garage')
      : buildLoginUrl(config);
    accountLabel.textContent = loggedIn
      ? 'View My Garage'
      : 'Sign in to save and sync your garage';
    accountLink.dataset.customerState = loggedIn ? 'logged-in' : 'logged-out';
    accountLink.hidden = loggedIn;
    accountLink.setAttribute('aria-hidden', loggedIn ? 'true' : 'false');
    accountLink.tabIndex = loggedIn ? -1 : 0;
  }

  function hideGarageElement(element) {
    if (!element) return;
    element.classList.add('sk-garage-history-hidden');
    element.setAttribute('aria-hidden', 'true');
  }

  function findLargestCompactAncestor(element, dialog, requiredText) {
    let current = element;
    let candidate = element;

    while (current && current.parentElement && current.parentElement !== dialog) {
      const parent = current.parentElement;
      const content = textOf(parent);

      if (
        content.length > 180 ||
        !content.toLowerCase().includes(requiredText.toLowerCase()) ||
        /\b(?:19|20)\d{2}\b/.test(content)
      ) {
        break;
      }

      candidate = parent;
      current = parent;
    }

    return candidate;
  }

  function findExactText(dialog, expectedText) {
    const normalized = expectedText.toLowerCase();

    return Array.from(
      dialog.querySelectorAll('a, button, summary, h1, h2, h3, h4, p, span, div')
    ).find(function (element) {
      return (
        !element.closest('[data-sk-saved-rides]') &&
        !element.classList.contains('sk-vehicle-modal__heading') &&
        textOf(element).toLowerCase() === normalized
      );
    });
  }

  function actionDescriptor(element) {
    return [
      element.getAttribute('aria-label'),
      element.getAttribute('title'),
      element.getAttribute('data-action'),
      element.getAttribute('name'),
      element.className,
      textOf(element)
    ].filter(Boolean).join(' ').toLowerCase();
  }

  function isVehicleAction(element, action) {
    const descriptor = actionDescriptor(element);

    if (action === 'delete') {
      return (
        /delete|remove|trash|dlt/.test(descriptor) ||
        Boolean(element.querySelector('svg[class*="trash"], .feather-trash-2'))
      );
    }

    return (
      /view|product|eye|detail/.test(descriptor) ||
      Boolean(element.querySelector('svg[class*="eye"], .feather-eye'))
    );
  }

  function getVehicleRows(dialog) {
    const controls = Array.from(
      dialog.querySelectorAll('a, button, input[type="button"], input[type="submit"]')
    ).filter(function (element) {
      return (
        !element.closest('[data-sk-saved-rides]') &&
        isVehicleAction(element, 'delete')
      );
    });
    const rows = [];

    controls.forEach(function (control) {
      let current = control.parentElement;

      while (current && current !== dialog) {
        if (/\b(?:19|20)\d{2}\b/.test(textOf(current))) {
          if (!rows.includes(current)) rows.push(current);
          break;
        }

        current = current.parentElement;
      }
    });

    return rows;
  }

  function extractVehicleLabel(row) {
    const candidates = Array.from(row.querySelectorAll('*'))
      .filter(function (element) {
        const value = textOf(element);
        return (
          /\b(?:19|20)\d{2}\b/.test(value) &&
          value.length <= 100 &&
          !element.matches('a, button, input') &&
          !element.querySelector('a, button, input')
        );
      })
      .map(textOf)
      .sort(function (a, b) {
        return a.length - b.length;
      });

    if (candidates.length) return candidates[0];

    const cleaned = textOf(row)
      .replace(/\b(?:View|Delete|Remove|Save|Products?)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const match = cleaned.match(/\b(?:19|20)\d{2}\b.*$/);

    return match ? match[0].trim() : cleaned;
  }

  function getVehicleEntries(dialog) {
    return getVehicleRows(dialog).map(function (row) {
      const controls = Array.from(row.querySelectorAll('a, button, input'));
      const label = extractVehicleLabel(row);
      const parsed = parseLegacyVehicle(label) || {};

      return {
        row: row,
        label: label,
        year: parsed.year || '',
        make: parsed.make || '',
        model: parsed.model || '',
        viewControl: controls.find(function (element) {
          return isVehicleAction(element, 'view');
        }),
        deleteControl: controls.find(function (element) {
          return isVehicleAction(element, 'delete');
        })
      };
    }).filter(function (entry) {
      return entry.label && /\b(?:19|20)\d{2}\b/.test(entry.label);
    });
  }

  function mergeVehicleEntries(sourceEntries) {
    const entries = readLocalRides().map(function (ride) {
      return {
        label: ride.label,
        year: ride.year,
        make: ride.make,
        model: ride.model,
        isLocal: true,
        row: null,
        viewControl: null,
        deleteControl: null
      };
    });
    const byLabel = new Map(entries.map(function (entry) {
      return [normalizeVehicleLabel(entry.label), entry];
    }));

    sourceEntries.forEach(function (sourceEntry) {
      const key = normalizeVehicleLabel(sourceEntry.label);
      const existing = byLabel.get(key);

      if (existing) {
        existing.row = sourceEntry.row;
        existing.viewControl = sourceEntry.viewControl;
        existing.deleteControl = sourceEntry.deleteControl;
        return;
      }

      entries.push(sourceEntry);
      byLabel.set(key, sourceEntry);
    });

    const selectedLabel = String(localStorage.getItem(STORAGE_KEY) || '').trim();
    const selectedKey = normalizeVehicleLabel(selectedLabel);

    if (selectedLabel && !byLabel.has(selectedKey)) {
      const legacyRide = parseLegacyVehicle(selectedLabel);

      if (legacyRide) {
        const entry = Object.assign({
          isLocal: true,
          row: null,
          viewControl: null,
          deleteControl: null
        }, legacyRide);

        entries.unshift(entry);
      }
    }

    return entries;
  }

  function waitForRideOption(dialog, index, expectedText) {
    return new Promise(function (resolve) {
      const startedAt = Date.now();
      const expected = normalizeVehicleLabel(expectedText);

      const selectWhenReady = function () {
        const selects = Array.from(dialog.querySelectorAll('select.ymm-filter, select'));
        const select = selects[index];
        const option = select && Array.from(select.options).find(function (candidate) {
          return normalizeVehicleLabel(candidate.textContent) === expected;
        });

        if (select && option) {
          select.value = option.value;
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
          resolve(true);
          return;
        }

        if (Date.now() - startedAt >= 2500) {
          resolve(false);
          return;
        }

        window.setTimeout(selectWhenReady, 60);
      };

      selectWhenReady();
    });
  }

  async function searchLocalRide(dialog, entry) {
    const values = [entry.year, entry.make, entry.model];

    if (values.some(function (value) { return !value; })) return;

    for (let index = 0; index < values.length; index += 1) {
      const selected = await waitForRideOption(dialog, index, values[index]);
      if (!selected) return;
    }

    const submit = dialog.querySelector('.sk-ride-form__submit');

    if (submit && !submit.disabled) submit.click();
  }

  function vehicleIconMarkup() {
    return [
      '<svg aria-hidden="true" viewBox="0 0 32 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">',
      '<path d="M4 17h2l2-7h4l3-6h8l4 8h1l2 5v2h-2"></path>',
      '<path d="M12 10l3-6"></path><path d="M23 4l4 8"></path>',
      '<path d="M12 10h15"></path><path d="M15 4h8"></path>',
      '<path d="M12 10v7"></path><path d="M23 10v7"></path>',
      '<path d="M12 17h10"></path>',
      '<circle cx="9" cy="19" r="3"></circle>',
      '<circle cx="25" cy="19" r="3"></circle>',
      '</svg>'
    ].join('');
  }

  function actionIconMarkup(action) {
    if (action === 'delete') {
      return '<svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M8 6V4h8v2"></path><path d="M19 6l-1 14H6L5 6"></path><path d="M10 11v5"></path><path d="M14 11v5"></path></svg>';
    }

    return '<svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z"></path><circle cx="12" cy="12" r="2.5"></circle></svg>';
  }

  function createProxyAction(entry, action) {
    const sourceControl = action === 'delete' ? entry.deleteControl : entry.viewControl;
    const button = document.createElement('button');
    const canSearchLocalRide = Boolean(entry.year && entry.make && entry.model);

    button.type = 'button';
    button.className = 'sk-saved-ride__action sk-saved-ride__action--' + action;
    button.title = action === 'delete' ? 'Delete vehicle' : 'View products';
    button.setAttribute(
      'aria-label',
      (action === 'delete' ? 'Delete ' : 'View products for ') + entry.label
    );
    button.innerHTML = action === 'view'
      ? '<span>View</span>' + actionIconMarkup(action)
      : actionIconMarkup(action);
    button.disabled = action === 'view' && !sourceControl && !canSearchLocalRide;
    button.addEventListener('click', function () {
      const dialog = button.closest('.sk-vehicle-modal__dialog');

      if (action === 'view') {
        localStorage.setItem(STORAGE_KEY, entry.label);

        const headerLabel = document.querySelector('[data-sk-vehicle-label]');
        if (headerLabel) headerLabel.textContent = entry.label;

        renderSavedRides(dialog);

        /*
         * View has one predictable meaning: apply this ride and execute the
         * native Globo search.  The old source eye sometimes only changed the
         * current garage row and did not navigate to filtered products.
         */
        if (canSearchLocalRide) searchLocalRide(dialog, entry);
        else if (sourceControl) sourceControl.click();

        return;
      }

      removeLocalRide(entry.label);

      if (
        normalizeVehicleLabel(localStorage.getItem(STORAGE_KEY)) ===
        normalizeVehicleLabel(entry.label)
      ) {
        localStorage.removeItem(STORAGE_KEY);

        const headerLabel = document.querySelector('[data-sk-vehicle-label]');
        if (headerLabel) headerLabel.textContent = 'Select Your Vehicle';
      }

      if (sourceControl) sourceControl.click();

      window.setTimeout(function () {
        renderSavedRides(dialog);
      }, sourceControl ? 120 : 0);
    });

    return button;
  }

  function createSavedRideCard(entry, isCurrent) {
    const card = document.createElement('article');
    const icon = document.createElement('span');
    const content = document.createElement('div');
    const meta = document.createElement('div');
    const vehicle = document.createElement('span');
    const actions = document.createElement('div');

    card.className = 'sk-saved-ride' + (isCurrent ? ' is-current' : '');
    icon.className = 'sk-saved-ride__icon';
    icon.innerHTML = vehicleIconMarkup();
    content.className = 'sk-saved-ride__content';
    meta.className = 'sk-saved-ride__meta';
    vehicle.className = 'sk-saved-ride__vehicle';
    vehicle.textContent = entry.label;
    actions.className = 'sk-saved-ride__actions';

    if (isCurrent) {
      const badge = document.createElement('span');
      badge.className = 'sk-saved-ride__badge';
      badge.textContent = 'CURRENT';
      meta.appendChild(badge);
    }

    content.appendChild(meta);
    content.appendChild(vehicle);
    actions.appendChild(createProxyAction(entry, 'view'));
    actions.appendChild(createProxyAction(entry, 'delete'));
    card.appendChild(icon);
    card.appendChild(content);
    card.appendChild(actions);

    return card;
  }

  function commonVehicleContainer(entries, dialog) {
    if (!entries.length) return null;

    let current = entries[0].row;

    while (
      current &&
      current !== dialog &&
      !entries.every(function (entry) { return current.contains(entry.row); })
    ) {
      current = current.parentElement;
    }

    return current && current !== dialog ? current : null;
  }

  function commonControlContainer(form, controls) {
    if (!controls.length) return form;

    let current = controls[0].parentElement;

    while (current && current !== form) {
      if (controls.every(function (control) { return current.contains(control); })) {
        return current;
      }

      current = current.parentElement;
    }

    return form;
  }

  function directLayoutItem(container, control) {
    let current = control;

    while (current.parentElement && current.parentElement !== container) {
      current = current.parentElement;
    }

    return current;
  }

  function prepareRideLayout(form) {
    const selects = Array.from(form.querySelectorAll('select.ymm-filter, select')).slice(0, 3);
    const submit = form.querySelector('.sk-ride-form__submit');

    if (selects.length < 3 || !submit) return;

    const controls = selects.concat(submit);
    const layout = commonControlContainer(form, controls);

    layout.classList.add('sk-ride-form__layout');

    selects.forEach(function (select, index) {
      const item = directLayoutItem(layout, select);
      item.classList.add('sk-ride-form__field', 'sk-ride-form__field--' + index);
    });

    directLayoutItem(layout, submit).classList.add('sk-ride-form__action');
  }

  function prepareRideForm(dialog) {
    const form = Array.from(dialog.querySelectorAll('form')).find(function (candidate) {
      return candidate.querySelectorAll('select.ymm-filter, select').length >= 3;
    });

    if (!form) return null;

    form.classList.add('sk-ride-form');

    Array.from(form.querySelectorAll('label')).forEach(function (label) {
      const value = textOf(label).toLowerCase();
      if (['year', 'make', 'model'].includes(value) && !label.querySelector('select, input')) {
        label.classList.add('sk-ride-field-label');
      }
    });

    Array.from(form.querySelectorAll('button, input[type="submit"], input[type="button"]'))
      .forEach(function (control) {
        const label = (textOf(control) || String(control.value || '')).toLowerCase();

        if (label === 'search' || label === 'save & search') {
          control.classList.add('sk-ride-form__submit');
          if (control.tagName === 'INPUT') control.value = 'Save & Search';
          else control.textContent = 'Save & Search';
        }

        if (label === 'reset') {
          control.classList.add('sk-ride-form__reset');

          let resetItem = control;

          while (resetItem.parentElement && resetItem.parentElement !== form) {
            resetItem = resetItem.parentElement;
          }

          hideGarageElement(
            resetItem !== control && textOf(resetItem).toLowerCase() === 'reset'
              ? resetItem
              : control
          );
        }
      });

    prepareRideLayout(form);

    let heading = dialog.querySelector('[data-sk-shop-ride-heading]');

    if (!heading) {
      heading = document.createElement('h3');
      heading.className = 'sk-garage-section-title sk-garage-section-title--shop';
      heading.setAttribute('data-sk-shop-ride-heading', '');
      heading.textContent = 'Shop for Your Ride';
      form.parentNode.insertBefore(heading, form);
    }

    return form;
  }

  function renderSavedRides(dialog) {
    const form = prepareRideForm(dialog);
    const sourceRoot = dialog._skSourceRoot || dialog;
    const sourceEntries = getVehicleEntries(sourceRoot);
    const entries = mergeVehicleEntries(sourceEntries);
    let section = dialog.querySelector('[data-sk-saved-rides]');

    if (!form || !entries.length) {
      if (section) section.hidden = true;
      return;
    }

    if (!section) {
      section = document.createElement('section');
      section.className = 'sk-saved-rides';
      section.setAttribute('data-sk-saved-rides', '');
      section.innerHTML = '<h3 class="sk-garage-section-title">Shop by Saved Ride</h3><div class="sk-saved-rides__grid" data-sk-saved-rides-grid></div>';
      form.parentNode.insertBefore(section, form.previousElementSibling || form);
    }

    section.hidden = false;

    const selectedVehicle = String(localStorage.getItem(STORAGE_KEY) || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const signature = entries.map(function (entry) {
      return entry.label;
    }).join('|') + '::' + selectedVehicle;
    const sourceRows = sourceEntries.map(function (entry) { return entry.row; });
    const sameSources = Array.isArray(section._skSourceRows) &&
      section._skSourceRows.length === sourceRows.length &&
      section._skSourceRows.every(function (row, index) {
        return row === sourceRows[index];
      });

    if (section.dataset.signature !== signature || !sameSources) {
      const grid = section.querySelector('[data-sk-saved-rides-grid]');
      grid.replaceChildren();

      entries.forEach(function (entry) {
        const normalizedLabel = entry.label.replace(/\s+/g, ' ').trim().toLowerCase();
        grid.appendChild(createSavedRideCard(entry, normalizedLabel === selectedVehicle));
      });

      section.dataset.signature = signature;
      section._skSourceRows = sourceRows;
    }

    if (sourceRoot !== dialog) return;

    const sourceContainer = commonVehicleContainer(sourceEntries, dialog);

    if (sourceContainer && !sourceContainer.contains(form)) {
      sourceContainer.classList.add('sk-garage-source-hidden');
      sourceContainer.setAttribute('aria-hidden', 'true');
    } else {
      sourceEntries.forEach(function (entry) {
        entry.row.classList.add('sk-garage-source-hidden');
        entry.row.setAttribute('aria-hidden', 'true');
      });
    }
  }

  function simplifyGarageHistory(dialog) {
    const garageLabel = findExactText(dialog, 'My Garage');

    if (garageLabel) {
      const interactiveHeader = garageLabel.closest(
        'button, summary, [role="button"], [aria-expanded]'
      );

      if (interactiveHeader?.getAttribute('aria-expanded') === 'false') {
        interactiveHeader.click();
      }

      hideGarageElement(
        interactiveHeader ||
        findLargestCompactAncestor(garageLabel, dialog, 'My Garage')
      );
    }

    const savedBikesNotice = Array.from(dialog.querySelectorAll('*')).find(
      function (element) {
        return textOf(element).toLowerCase() === 'view your saved bikes!';
      }
    );

    if (savedBikesNotice) {
      hideGarageElement(
        findLargestCompactAncestor(savedBikesNotice, dialog, 'View your saved bikes!')
      );
    }

    const recentHeading = findExactText(dialog, 'Recent Search History');

    if (recentHeading) {
      hideGarageElement(
        findLargestCompactAncestor(recentHeading, dialog, 'Recent Search History')
      );
    }

    const clearAll = findExactText(dialog, 'Clear All Vehicles');
    hideGarageElement(clearAll?.closest('a, button') || clearAll);

    Array.from(dialog.querySelectorAll('button, a, input[type="button"], input[type="submit"]'))
      .filter(function (element) {
        const label = (textOf(element) || String(element.value || '')).toLowerCase();
        return label === 'save' || label === 'add a vehicle';
      })
      .forEach(hideGarageElement);

    dialog.classList.add('sk-garage-history-simplified');
    renderSavedRides(dialog);
  }

  function watchGarageHistory(dialog) {
    if (dialog.dataset.skGarageHistoryWatching === 'true') return;

    let scheduled = false;
    const scheduleSimplify = function () {
      if (scheduled) return;
      scheduled = true;

      window.requestAnimationFrame(function () {
        scheduled = false;
        simplifyGarageHistory(dialog);
      });
    };
    const observer = new MutationObserver(scheduleSimplify);

    observer.observe(dialog, { childList: true, subtree: true });
    dialog.dataset.skGarageHistoryWatching = 'true';
    scheduleSimplify();
  }

  function createAccountRow(dialog) {
    const existing = dialog.querySelector('[data-sk-garage-account-row]');

    if (existing) return existing;

    const config = getThemeConfig();
    const row = document.createElement('div');
    const shopLink = document.createElement('a');
    const accountLink = document.createElement('a');

    row.className = 'sk-garage-account-row';
    row.setAttribute('data-sk-garage-account-row', '');

    shopLink.className = 'sk-garage-account-row__shop';
    shopLink.href = config.shopUrl || '/collections/all';
    shopLink.innerHTML = [
      '<span class="sk-garage-account-row__help" aria-hidden="true">?</span>',
      '<span>Shop Without a Ride</span>'
    ].join('');

    accountLink.className = 'sk-garage-account-row__account';
    accountLink.setAttribute('data-sk-garage-account-link', '');
    accountLink.innerHTML = [
      '<svg aria-hidden="true" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">',
      '<path d="M20 21a8 8 0 0 0-16 0"></path>',
      '<circle cx="12" cy="7" r="4"></circle>',
      '</svg>',
      '<span data-sk-garage-account-label></span>'
    ].join('');

    row.appendChild(shopLink);
    row.appendChild(accountLink);

    const form = dialog.querySelector('form');

    if (form && form.parentNode) {
      form.parentNode.insertBefore(row, form.nextSibling);
    } else {
      dialog.appendChild(row);
    }

    updateAccountRow(dialog);

    return row;
  }

  function enhanceDialog(dialog) {
    if (dialog.dataset.skGarageEnhanced === 'true') return;

    const headings = Array.from(dialog.querySelectorAll('h1, h2, h3, h4'));
    let heading = headings.find(function (element) {
      return textOf(element).toLowerCase() === 'select your vehicle';
    });

    if (!heading) {
      heading = document.createElement('h2');
      heading.textContent = 'My Garage';
      dialog.insertBefore(heading, dialog.firstChild);
    } else {
      heading.textContent = 'My Garage';
    }

    heading.classList.add('sk-vehicle-modal__heading');

    dialog.querySelector('[data-sk-vehicle-subtitle]')?.remove();

    createAccountRow(dialog);
    dialog.dataset.skGarageEnhanced = 'true';
  }

 function findHeaderSearchInput() {
  const selectors = [
    '.sk-header-search__input',
    'header input[type="search"][name="q"]',
    'header input[type="search"]',
    'input[type="search"][placeholder*="Search products" i]'
  ];

  for (const selector of selectors) {
    const element = document.querySelector(selector);

    if (element) {
      return element;
    }
  }

  return null;
}

function findYmmSection() {
  const ymmSelects = Array.from(
    document.querySelectorAll('select.ymm-filter')
  );

  if (!ymmSelects.length) {
    return null;
  }

  /*
   * 只允许 Header group（sections--）中的 Show YMMbox 成为顶部弹窗。
   * 首页 Template（template--）中的第二套 YMM 必须留在原位，作为
   * CAN-AM / POLARIS 品牌区上方的 Find Your Ride。
   */
  const targetSelect = ymmSelects.find(function (select) {
    const headerSection = select.closest(
      '[id^="shopify-section-sections--"]'
    );

    return Boolean(
      headerSection &&
      !select.closest('#MainContent, main') &&
      !select.closest('.ymm-garage-page, .my_grg_pg, .ymm_recent_wrap')
    );
  });

  if (!targetSelect) {
    console.warn(
      '[SK Vehicle Search] Header-group YMM was not found; Template Find Your Ride was left untouched.'
    );
    return null;
  }

  targetSelect.setAttribute('data-sk-header-ymm-source', '');

  /*
   * 返回 Header group 的完整 Apps section。只给这一套 YMM 加弹窗样式，
   * 绝不修改首页 template-- 的 Find Your Ride section。
   */
  return targetSelect.closest('[id^="shopify-section-sections--"]');
}

  function createVehicleButton() {
    const existing = document.getElementById(BUTTON_ID);

    if (existing) return existing;

    const button = document.createElement('button');

    button.id = BUTTON_ID;
    button.type = 'button';
    button.className = 'sk-vehicle-trigger';
    button.setAttribute('data-sk-vehicle-trigger', '');
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');

    button.innerHTML = [
      '<span class="sk-vehicle-trigger__icon" aria-hidden="true">' +
        vehicleIconMarkup() +
      '</span>',
      '<span class="sk-vehicle-trigger__label" data-sk-vehicle-label>',
      'Select Your Vehicle',
      '</span>',
      '<span class="sk-vehicle-trigger__arrow" aria-hidden="true">›</span>'
    ].join('');

    button.querySelector('.sk-vehicle-trigger__arrow').textContent = '›';

    const savedVehicle = localStorage.getItem(STORAGE_KEY);

    if (savedVehicle) {
      button.querySelector('[data-sk-vehicle-label]').textContent =
        savedVehicle;
    }

    return button;
  }

function insertVehicleButton(button, searchInput) {
  const form = searchInput.closest('form');

  if (!form || !form.parentNode) {
    return false;
  }

  /*
   * 车型按钮不能放进搜索 form，
   * 否则会触发主题原生搜索弹窗。
   */
  let wrapper = form.closest('.sk-combined-search');

  if (!wrapper) {
    wrapper = document.createElement('div');
    wrapper.className = 'sk-combined-search';

    form.parentNode.insertBefore(wrapper, form);
    wrapper.appendChild(form);
  }

  form.classList.add('sk-product-search-form');

  if (!wrapper.contains(button)) {
    wrapper.insertBefore(button, form);
  }

  return true;
}

function getSourceYmmSelects(sourceRoot) {
  return Array.from(sourceRoot.querySelectorAll('select.ymm-filter')).slice(0, 3);
}

function findSourceSearchControl(sourceRoot) {
  return Array.from(
    sourceRoot.querySelectorAll('button, input[type="submit"], input[type="button"]')
  ).find(function (control) {
    const label = (textOf(control) || String(control.value || '')).toLowerCase();
    return label === 'search' || label === 'save & search';
  }) || null;
}

function createModal(ymmSection) {
  const existing = document.querySelector(
    '[data-sk-vehicle-modal-root]'
  );

  if (existing) {
    const existingDialog = existing.querySelector('.sk-vehicle-modal__dialog');

    if (existingDialog) enhanceDialog(existingDialog);
    return existing;
  }

  /*
   * 不再把 Globo Section 移动到 body。
   * 移动节点会导致 Globo 自定义下拉界面失效。
   */
  /* Always start closed, even if the App reused an old open-state class. */
  ymmSection.classList.remove('is-open', 'open', 'active');
  ymmSection.setAttribute(
    'data-sk-vehicle-modal-root',
    ''
  );

  ymmSection.classList.add(
    'sk-vehicle-modal',
    'sk-vehicle-modal--original-section'
  );

  ymmSection.setAttribute('aria-hidden', 'true');

  /*
   * 找到 App Section 中原有的可视内容容器。
   */
  const sourceSelect = ymmSection.querySelector('[data-sk-header-ymm-source]');
  let dialog = sourceSelect;

  while (dialog && dialog.parentElement !== ymmSection) {
    dialog = dialog.parentElement;
  }

  if (!dialog) {
    dialog = Array.from(ymmSection.children).find(
      function (element) {
        return ![
          'STYLE',
          'SCRIPT',
          'LINK',
          'TEMPLATE'
        ].includes(element.tagName);
      }
    );
  }

  if (!dialog) {
    console.warn(
      '[SK Vehicle Search] YMM dialog wrapper not found.'
    );

    return ymmSection;
  }

  dialog.classList.add(
    'sk-vehicle-modal__dialog',
    'sk-vehicle-modal__content'
  );

  enhanceDialog(dialog);

  /*
   * 在原 App 容器中添加关闭按钮。
   */
  if (!dialog.querySelector('[data-sk-vehicle-close]')) {
    const closeButton = document.createElement('button');

    closeButton.type = 'button';
    closeButton.className = 'sk-vehicle-modal__close';
    closeButton.setAttribute(
      'data-sk-vehicle-close',
      ''
    );
    closeButton.setAttribute(
      'aria-label',
      'Close'
    );
    closeButton.textContent = '×';

    dialog.insertBefore(
      closeButton,
      dialog.firstChild
    );
  }

  return ymmSection;
}

  function openModal(modal, button) {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    button.setAttribute('aria-expanded', 'true');

    document.documentElement.classList.add('sk-vehicle-modal-open');
    document.body.classList.add('sk-vehicle-modal-open');

    const dialog = modal.querySelector('.sk-vehicle-modal__dialog') || modal;

    updateAccountRow(dialog);

    /*
     * Globo 会在控件第一次变为可见时计算下拉框尺寸并加载历史车型。
     * 先显示弹窗，再于连续两帧后执行 V5 重排，避免首次点击出现空白/挤压控件。
     */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        window.dispatchEvent(new Event('resize'));
        watchGarageHistory(dialog);
        simplifyGarageHistory(dialog);

        [80, 240, 600].forEach(function (delay) {
          window.setTimeout(function () {
            updateAccountRow(dialog);
            simplifyGarageHistory(dialog);
          }, delay);
        });
      });
    });

    const firstControl = dialog.querySelector(
      'select, button:not([data-sk-vehicle-close]), input'
    );

    window.setTimeout(function () {
      firstControl?.focus();
    }, 100);
  }

  function closeModal(modal, button) {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    button.setAttribute('aria-expanded', 'false');

    document.documentElement.classList.remove('sk-vehicle-modal-open');
    document.body.classList.remove('sk-vehicle-modal-open');

    button.focus();
  }

  function getNativeSelectValues(modal) {
    const selects = Array.from(modal.querySelectorAll('select')).filter(
      function (select) {
        return !select.disabled;
      }
    );

    if (selects.length < 3) return [];

    return selects.slice(0, 3).map(function (select) {
      const option = select.options[select.selectedIndex];

      if (!select.value || !option) return '';

      return textOf(option);
    });
  }

  function getCustomSelectValues(modal) {
    const possibleLabels = modal.querySelectorAll(
      [
        '[data-ymm-level] .selected',
        '[data-ymm-level] [class*="label"]',
        '[data-ymm-level] [class*="selected"]',
        '.gf-form [class*="selected"]',
        '.gf-form [class*="label"]'
      ].join(',')
    );

    return Array.from(possibleLabels)
      .map(textOf)
      .filter(function (value) {
        const normalized = value.toLowerCase();

        return (
          value &&
          !normalized.includes('select year') &&
          !normalized.includes('select make') &&
          !normalized.includes('select model')
        );
      })
      .slice(0, 3);
  }

  function updateSelectedVehicle(modal, button, saveHistory) {
    let values = getNativeSelectValues(modal);

    if (values.filter(Boolean).length < 3) {
      values = getCustomSelectValues(modal);
    }

    values = values.filter(Boolean);

    if (values.length < 3) return null;

    const vehicleLabel = values.slice(0, 3).join(' ');
    const label = button.querySelector('[data-sk-vehicle-label]');

    label.textContent = vehicleLabel;
    localStorage.setItem(STORAGE_KEY, vehicleLabel);

    return saveHistory ? saveLocalRide(values.slice(0, 3)) : {
      label: vehicleLabel,
      year: values[0],
      make: values[1],
      model: values[2]
    };
  }

  function saveSourceVehicle(sourceRoot, button, dialog) {
    const values = getSourceYmmSelects(sourceRoot).map(function (select) {
      const option = select.options[select.selectedIndex];
      return select.value && option ? textOf(option) : '';
    });

    if (values.filter(Boolean).length < 3) return;

    const ride = saveLocalRide(values);

    if (!ride) return;

    localStorage.setItem(STORAGE_KEY, ride.label);

    const headerLabel = button.querySelector('[data-sk-vehicle-label]');
    if (headerLabel) headerLabel.textContent = ride.label;

    renderSavedRides(dialog);
  }

  function bindSourceYmmHistory(sourceRoot, button, dialog) {
    if (sourceRoot.dataset.skSourceHistoryBound === 'true') return;

    sourceRoot.addEventListener('click', function (event) {
      const control = event.target.closest(
        'button, input[type="submit"], input[type="button"]'
      );

      if (!control) return;

      const label = (textOf(control) || String(control.value || '')).toLowerCase();

      if (label === 'search' || label === 'save & search') {
        saveSourceVehicle(sourceRoot, button, dialog);
      }
    }, true);

    sourceRoot.dataset.skSourceHistoryBound = 'true';
  }

  function selectOptionSignature(select) {
    return Array.from(select.options).map(function (option) {
      return [option.value, textOf(option), option.disabled ? '1' : '0'].join('::');
    }).join('|');
  }

  function copyYmmSelectState(source, target) {
    if (!source || !target || source === target) return;

    const sourceSignature = selectOptionSignature(source);

    if (selectOptionSignature(target) !== sourceSignature) {
      const options = Array.from(source.options).map(function (option) {
        return option.cloneNode(true);
      });

      target.replaceChildren.apply(target, options);
    }

    target.disabled = source.disabled;
    target.value = source.value;
  }

  function syncTemplateYmmState(headerRoot, templateRoot) {
    const sourceSelects = getSourceYmmSelects(headerRoot);
    const templateSelects = Array.from(
      templateRoot.querySelectorAll('[data-sk-template-ymm-level]')
    );

    sourceSelects.forEach(function (source, index) {
      copyYmmSelectState(source, templateSelects[index]);
    });

    const submit = templateRoot.querySelector('[data-sk-template-ymm-search]');
    const sourceSubmit = findSourceSearchControl(headerRoot);
    const hasCompleteRide = templateSelects.length === 3 &&
      templateSelects.every(function (select) {
        return !select.disabled && Boolean(select.value);
      });

    if (submit) {
      submit.disabled = !hasCompleteRide || !sourceSubmit || sourceSubmit.disabled;
    }
  }

  function scheduleTemplateYmmSync(headerRoot, templateRoot) {
    if (Array.isArray(templateRoot._skYmmSyncTimers)) {
      templateRoot._skYmmSyncTimers.forEach(function (timer) {
        window.clearTimeout(timer);
      });
    }

    templateRoot._skYmmSyncTimers = [0, 60, 180, 420, 900].map(
      function (delay) {
        return window.setTimeout(function () {
          syncTemplateYmmState(headerRoot, templateRoot);
        }, delay);
      }
    );
  }

  function findMatchingYmmOption(select, value, label) {
    const normalizedLabel = normalizeVehicleLabel(label);

    return Array.from(select.options).find(function (option) {
      return String(option.value) === String(value);
    }) || Array.from(select.options).find(function (option) {
      return normalizeVehicleLabel(textOf(option)) === normalizedLabel;
    }) || null;
  }

  function findResetControl(sourceRoot) {
    return Array.from(
      sourceRoot.querySelectorAll('button, input[type="button"], input[type="reset"]')
    ).find(function (control) {
      return (textOf(control) || String(control.value || '')).toLowerCase() === 'reset';
    }) || null;
  }

  function bindTemplateYmmSync(headerRoot, templateRoot) {
    if (templateRoot.dataset.skTemplateYmmSyncBound === 'true') return;

    templateRoot.addEventListener('change', function (event) {
      const templateSelect = event.target.closest('select.ymm-filter');

      if (!templateSelect || !templateRoot.contains(templateSelect)) return;

      const templateSelects = getSourceYmmSelects(templateRoot);
      const sourceSelects = getSourceYmmSelects(headerRoot);
      const index = templateSelects.indexOf(templateSelect);
      const sourceSelect = sourceSelects[index];
      const selectedOption = templateSelect.options[templateSelect.selectedIndex];

      if (index < 0 || !sourceSelect || !selectedOption) return;

      /*
       * 阻止 Globo 的第二实例继续用重复 ID 操作第一实例，
       * 改为明确驱动 Header 中已初始化的原生控件。
       */
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const sourceOption = findMatchingYmmOption(
        sourceSelect,
        templateSelect.value,
        textOf(selectedOption)
      );

      if (!sourceOption) return;

      sourceSelect.value = sourceOption.value;
      sourceSelect.dispatchEvent(new Event('input', { bubbles: true }));
      sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
      scheduleTemplateYmmSync(headerRoot, templateRoot);
    }, true);

    templateRoot.addEventListener('click', function (event) {
      const control = event.target.closest(
        'button, input[type="submit"], input[type="button"], input[type="reset"]'
      );

      if (!control || !templateRoot.contains(control)) return;

      const label = (textOf(control) || String(control.value || '')).toLowerCase();

      if (label !== 'search' && label !== 'save & search' && label !== 'reset') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const sourceControl = label === 'reset'
        ? findResetControl(headerRoot)
        : findSourceSearchControl(headerRoot);

      if (sourceControl && !sourceControl.disabled) {
        sourceControl.click();
      }

      scheduleTemplateYmmSync(headerRoot, templateRoot);
    }, true);

    const observer = new MutationObserver(function () {
      scheduleTemplateYmmSync(headerRoot, templateRoot);
    });

    observer.observe(headerRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    templateRoot.dataset.skTemplateYmmSyncBound = 'true';
    scheduleTemplateYmmSync(headerRoot, templateRoot);
  }

  function createTemplateYmmField(label, index) {
    const field = document.createElement('label');
    const caption = document.createElement('span');
    const select = document.createElement('select');

    field.className = 'sk-template-ymm__field';
    caption.className = 'sk-template-ymm__label';
    caption.textContent = label;
    select.className = 'sk-template-ymm__select';
    select.setAttribute('data-sk-template-ymm-level', String(index));
    select.setAttribute('aria-label', label);
    field.appendChild(caption);
    field.appendChild(select);

    return field;
  }

  function createTemplateYmmForm() {
    const form = document.createElement('form');
    const fields = document.createElement('div');
    const actions = document.createElement('div');
    const search = document.createElement('button');
    const reset = document.createElement('button');

    form.className = 'sk-template-ymm';
    form.setAttribute('data-sk-template-ymm-form', '');
    form.noValidate = true;
    fields.className = 'sk-template-ymm__fields';
    actions.className = 'sk-template-ymm__actions';

    ['Year', 'Make', 'Model'].forEach(function (label, index) {
      fields.appendChild(createTemplateYmmField(label, index));
    });

    search.type = 'submit';
    search.className = 'sk-template-ymm__search';
    search.setAttribute('data-sk-template-ymm-search', '');
    search.textContent = 'Search';
    search.disabled = true;

    reset.type = 'button';
    reset.className = 'sk-template-ymm__reset';
    reset.setAttribute('data-sk-template-ymm-reset', '');
    reset.textContent = 'Reset';

    actions.appendChild(search);
    actions.appendChild(reset);
    fields.appendChild(actions);
    form.appendChild(fields);

    return form;
  }

  function isolateTemplateYmmIds(templateRoot) {
    Array.from(templateRoot.querySelectorAll('[id]')).forEach(function (element) {
      if (/^(?:ymm_|dropdown-field)/i.test(element.id)) {
        element.removeAttribute('id');
      }
    });
  }

  function bindTemplateYmmProxy(headerRoot, templateRoot, button, dialog) {
    if (templateRoot.dataset.skTemplateYmmProxyBound === 'true') {
      scheduleTemplateYmmSync(headerRoot, templateRoot);
      return true;
    }

    const nativeForm = Array.from(templateRoot.querySelectorAll('form')).find(
      function (candidate) {
        return candidate.querySelectorAll('select.ymm-filter').length >= 3;
      }
    );

    if (!nativeForm || !nativeForm.parentNode) return false;

    const templateForm = createTemplateYmmForm();
    const heading = Array.from(
      templateRoot.querySelectorAll('h1, h2, h3, h4')
    ).find(function (candidate) {
      return textOf(candidate).toLowerCase() === 'find your ride';
    });

    nativeForm.parentNode.replaceChild(templateForm, nativeForm);
    isolateTemplateYmmIds(templateRoot);

    if (heading) {
      heading.classList.add('sk-template-ymm__heading');
      heading.style.removeProperty('display');
    }

    templateForm.addEventListener('change', function (event) {
      const templateSelect = event.target.closest('[data-sk-template-ymm-level]');

      if (!templateSelect) return;

      const index = Number(templateSelect.getAttribute('data-sk-template-ymm-level'));
      const sourceSelect = getSourceYmmSelects(headerRoot)[index];
      const selectedOption = templateSelect.options[templateSelect.selectedIndex];

      if (!sourceSelect || !selectedOption) return;

      const sourceOption = findMatchingYmmOption(
        sourceSelect,
        templateSelect.value,
        textOf(selectedOption)
      );

      sourceSelect.value = sourceOption ? sourceOption.value : '';
      sourceSelect.dispatchEvent(new Event('input', { bubbles: true }));
      sourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
      scheduleTemplateYmmSync(headerRoot, templateRoot);
    });

    templateForm.addEventListener('submit', function (event) {
      event.preventDefault();

      const sourceSearch = findSourceSearchControl(headerRoot);
      const templateSearch = templateRoot.querySelector(
        '[data-sk-template-ymm-search]'
      );

      if (!sourceSearch || sourceSearch.disabled || templateSearch.disabled) return;

      saveSourceVehicle(headerRoot, button, dialog);
      sourceSearch.click();
    });

    templateForm.querySelector('[data-sk-template-ymm-reset]')
      .addEventListener('click', function () {
        const sourceReset = findResetControl(headerRoot);

        if (sourceReset) sourceReset.click();
        scheduleTemplateYmmSync(headerRoot, templateRoot);
      });

    const observer = new MutationObserver(function () {
      scheduleTemplateYmmSync(headerRoot, templateRoot);
    });

    observer.observe(headerRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    templateRoot.dataset.skTemplateYmmProxyBound = 'true';
    templateRoot.setAttribute('data-sk-template-ymm-ready', '');
    scheduleTemplateYmmSync(headerRoot, templateRoot);

    return true;
  }

  function bindTemplateYmmHistories(headerRoot, button, dialog) {
    const templateSections = Array.from(
      document.querySelectorAll('[id^="shopify-section-template--"]')
    );

    templateSections.forEach(function (section) {
      if (section === headerRoot || headerRoot.contains(section)) return;

      const selects = section.querySelectorAll('select.ymm-filter');

      if (selects.length < 3) return;

      /*
       * Globo 的两个 Searchbox Block 会生成重复的 ymm_form / ymm_label ID。
       * 它只会自动展开第一个实例，因此第二个 Template 实例虽然已有完整
       * Year / Make / Model，.dropdowns 仍会保留内联 display:none。
       * 这里只标记现有 Template Section，交给作用域 CSS 将其恢复显示；
       * 不复制表单、不创建新的 select，也不会产生第三套 YMM。
       */
      section.setAttribute('data-sk-template-ymm', '');

      /*
       * 首页的 Find Your Ride 保持原 DOM、原样式和原 Globo 行为。
      * 这里仅监听其 Search，用于同步未登录访客的本地多车型历史。
       */
      bindTemplateYmmProxy(headerRoot, section, button, dialog);
    });
  }

  function bindEvents(modal, button) {
    button.addEventListener(
  'pointerdown',
  function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  },
  true
);

button.addEventListener(
  'click',
  function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    openModal(modal, button);
  },
  true
);

    modal
      .querySelector('[data-sk-vehicle-close]')
      ?.addEventListener('click', function () {
        closeModal(modal, button);
      });

    modal.addEventListener('click', function (event) {
      if (event.target === modal) {
        closeModal(modal, button);
      }
    });

    modal.addEventListener('change', function () {
      updateSelectedVehicle(modal, button);
    });

    modal.addEventListener('click', function (event) {
      const target = event.target.closest('button, input[type="submit"]');

      if (!target) return;

      const targetText = (
        textOf(target) ||
        String(target.value || '')
      ).toLowerCase();

      if (targetText === 'reset') {
        localStorage.removeItem(STORAGE_KEY);

        button.querySelector(
          '[data-sk-vehicle-label]'
        ).textContent = 'Select Your Vehicle';

        return;
      }

      if (targetText === 'search' || targetText === 'save & search') {
        updateSelectedVehicle(modal, button, true);
        renderSavedRides(modal);
      }
    }, true);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal(modal, button);
      }
    });
  }

function initialize() {
  if (initialized) return true;

  const searchInput = findHeaderSearchInput();
  const ymmSection = findYmmSection();

  if (!searchInput || !ymmSection) {
    return false;
  }

  const ymmSelects = Array.from(
    ymmSection.querySelectorAll('select.ymm-filter')
  );

  /*
   * 必须等 Globo 完成 YMM 初始化。
   * 正常情况下 Year 下拉框至少应该包含占位项和年份选项。
   */
  if (ymmSelects.length < 3) {
    return false;
  }

  const yearSelect = ymmSelects[0];

  if (!yearSelect || yearSelect.options.length <= 1) {
    console.log(
      '[SK Vehicle Search] Waiting for Globo YMM data...',
      {
        selectCount: ymmSelects.length,
        yearOptionCount: yearSelect
          ? yearSelect.options.length
          : 0
      }
    );

    return false;
  }

  console.log('[SK Vehicle Search] Globo YMM is ready', {
    searchInput: searchInput,
    ymmSection: ymmSection,
    selectCount: ymmSelects.length,
    yearOptionCount: yearSelect.options.length
  });

  const button = createVehicleButton();

  if (!insertVehicleButton(button, searchInput)) {
    return false;
  }

  /*
   * 只把 Header group 的原生 YMM section 显示为顶部弹窗。
   * Template 中的第二套 YMM 留在 CAN-AM / POLARIS 品牌区上方，
   * 不复制、不移动，也不会再创建第三套代理表单。
   */
  const modal = createModal(ymmSection);
  const dialog = modal.querySelector('.sk-vehicle-modal__dialog') || modal;

  bindEvents(modal, button);
  bindSourceYmmHistory(ymmSection, button, dialog);
  bindTemplateYmmHistories(ymmSection, button, dialog);
  initialized = true;

  return true;
}



 function boot() {
  if (initialize()) return;

  let attempts = 0;
  const maxAttempts = 300;

  const timer = window.setInterval(function () {
    attempts += 1;

    if (initialize()) {
      window.clearInterval(timer);
      return;
    }

    if (attempts >= maxAttempts) {
      console.warn(
        '[SK Vehicle Search] Globo YMM did not become ready.'
      );

      window.clearInterval(timer);
    }
  }, 100);
}
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    initialized = false;
    window.setTimeout(boot, 100);
  });
})();
